import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchBoardTasks, type TaskRow } from '../api/tasks';
import {
  createDefaultProjectBoardColumns,
  fetchProjectBoardLayout,
  loadProjectBoardLayoutLegacy,
  type ProjectBoardColumnDef,
} from '../api/projectBoard';
import { PROJECT_BOARD_FIRST_COLUMN_ID } from '../constants/projectBoard';
import './ProjectTimelineTab.css';

interface ProjectTimelineTabProps {
  projectId: number;
  eventId?: number;
  viewerUserId: number;
}

function parseCalendarDate(value: string | null | undefined): Date | null {
  if (value == null || !String(value).trim()) return null;
  const s = String(value).trim();
  const cal = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (cal) {
    const y = parseInt(cal[1], 10);
    const mo = parseInt(cal[2], 10) - 1;
    const da = parseInt(cal[3], 10);
    return new Date(y, mo, da, 12, 0, 0, 0);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function taskTimeRange(task: TaskRow): { startMs: number; endMs: number } {
  const start =
    parseCalendarDate(task.start_date) ??
    (() => {
      const c = new Date(task.created_at);
      return Number.isNaN(c.getTime()) ? new Date() : c;
    })();
  let startMs = start.getTime();
  let end = parseCalendarDate(task.due_date);
  if (end == null) {
    end = new Date(startMs);
    end.setDate(end.getDate() + 1);
  }
  let endMs = end.getTime();
  if (endMs <= startMs) {
    endMs = startMs + 86400000;
  }
  return { startMs, endMs };
}

function assigneeLabel(task: TaskRow): string {
  if (task.assignees && task.assignees.length > 0) {
    return task.assignees.map((a) => a.name).join(', ');
  }
  if (task.assigned_to_name?.trim()) return task.assigned_to_name.trim();
  return 'Unassigned';
}

/** Order tasks so each dependent appears directly under its predecessor row (forest, DFS pre-order). */
function sortTasksByDependencyTree(tasks: TaskRow[]): TaskRow[] {
  if (tasks.length <= 1) return [...tasks];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const children = new Map<number, TaskRow[]>();

  for (const t of tasks) {
    const pid = t.depends_on_task_id;
    if (pid != null && byId.has(pid)) {
      let list = children.get(pid);
      if (!list) {
        list = [];
        children.set(pid, list);
      }
      list.push(t);
    }
  }
  children.forEach((arr) => {
    arr.sort((a: TaskRow, b: TaskRow) => {
      const ta = taskTimeRange(a).startMs;
      const tb = taskTimeRange(b).startMs;
      if (ta !== tb) return ta - tb;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  });

  const roots = tasks.filter((t) => {
    const pid = t.depends_on_task_id;
    return pid == null || !byId.has(pid);
  });
  roots.sort((a, b) => {
    const ta = taskTimeRange(a).startMs;
    const tb = taskTimeRange(b).startMs;
    if (ta !== tb) return ta - tb;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  const out: TaskRow[] = [];
  const visiting = new Set<number>();

  function visit(node: TaskRow) {
    if (visiting.has(node.id)) return;
    visiting.add(node.id);
    out.push(node);
    const ch = children.get(node.id);
    if (ch) {
      for (const c of ch) visit(c);
    }
  }

  for (const r of roots) visit(r);

  if (out.length < tasks.length) {
    const seen = new Set(out.map((t) => t.id));
    const rest = tasks.filter((t) => !seen.has(t.id));
    rest.sort((a, b) => {
      const ta = taskTimeRange(a).startMs;
      const tb = taskTimeRange(b).startMs;
      if (ta !== tb) return ta - tb;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    out.push(...rest);
  }

  return out;
}

const TICK_COUNT = 8;

function barStyle(
  task: TaskRow,
  rangeMin: number,
  rangeMax: number
): { leftPct: number; widthPct: number; className: string } {
  const { startMs, endMs } = taskTimeRange(task);
  const span = Math.max(1, rangeMax - rangeMin);
  const left = ((startMs - rangeMin) / span) * 100;
  const width = ((endMs - startMs) / span) * 100;
  const leftPct = Math.max(0, Math.min(100, left));
  const widthPct = Math.max(0.35, Math.min(100 - leftPct, width));

  const col = task.project_board_column_id ?? '';
  let className = 'project-gantt-bar';
  if (task.status === 'Void') {
    className += ' project-gantt-bar--void';
  } else if (col === 'col-done' || task.status === 'Completed') {
    className += ' project-gantt-bar--done';
  } else if (col === 'col-doing' || task.status === 'In Progress') {
    className += ' project-gantt-bar--doing';
  } else if (col === PROJECT_BOARD_FIRST_COLUMN_ID || task.status === 'To-Do') {
    className += ' project-gantt-bar--todo';
  } else {
    className += ' project-gantt-bar--default';
  }

  return { leftPct, widthPct, className };
}

function formatTick(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface DepLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const ProjectTimelineTab: React.FC<ProjectTimelineTabProps> = ({ projectId, eventId, viewerUserId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [depLines, setDepLines] = useState<DepLine[]>([]);
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 });

  const bodyRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const markerId = `project-gantt-arrow-${projectId}`;

  /** Section titles come from the project's shared board layout so they read the same for everyone. */
  const [boardColumnDefs, setBoardColumnDefs] = useState<ProjectBoardColumnDef[]>(() =>
    createDefaultProjectBoardColumns()
  );

  useEffect(() => {
    let cancelled = false;
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) return;
    fetchProjectBoardLayout(projectId, viewerUserId)
      .then((layout) => {
        if (cancelled) return;
        setBoardColumnDefs(
          layout?.columns ?? loadProjectBoardLayoutLegacy(projectId)?.columns ?? createDefaultProjectBoardColumns()
        );
      })
      .catch(() => {
        /* Fall back to the default titles. */
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, viewerUserId]);

  const boardColumnTitleById = useMemo(
    () => new Map(boardColumnDefs.map((c) => [c.id, c.title])),
    [boardColumnDefs]
  );

  const load = useCallback(async () => {
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const list = await fetchBoardTasks(viewerUserId, { projectId, eventId });
      setTasks(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, eventId, viewerUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const { rangeMin, rangeMax, ticks, sortedTasks } = useMemo(() => {
    if (tasks.length === 0) {
      const now = Date.now();
      const pad = 7 * 86400000;
      const rMin = now - pad;
      const rMax = now + pad;
      const tickMs: number[] = [];
      for (let i = 0; i < TICK_COUNT; i += 1) {
        tickMs.push(rMin + ((rMax - rMin) * i) / (TICK_COUNT - 1));
      }
      return { rangeMin: rMin, rangeMax: rMax, ticks: tickMs, sortedTasks: [] as TaskRow[] };
    }
    let min = Infinity;
    let max = -Infinity;
    for (const t of tasks) {
      const { startMs, endMs } = taskTimeRange(t);
      if (startMs < min) min = startMs;
      if (endMs > max) max = endMs;
    }
    const pad = Math.max(86400000, (max - min) * 0.05);
    const rMin = min - pad;
    const rMax = max + pad;
    const tickMs: number[] = [];
    for (let i = 0; i < TICK_COUNT; i += 1) {
      tickMs.push(rMin + ((rMax - rMin) * i) / (TICK_COUNT - 1));
    }
    const sorted = sortTasksByDependencyTree(tasks);
    return { rangeMin: rMin, rangeMax: rMax, ticks: tickMs, sortedTasks: sorted };
  }, [tasks]);

  const measureDependencyLines = useCallback(() => {
    const wrap = bodyRef.current;
    if (!wrap || sortedTasks.length === 0) {
      setDepLines([]);
      setSvgSize({ w: 0, h: 0 });
      return;
    }
    const cr = wrap.getBoundingClientRect();
    const lines: DepLine[] = [];
    for (const task of sortedTasks) {
      if (task.depends_on_task_id == null) continue;
      const pred = barRefs.current.get(task.depends_on_task_id);
      const succ = barRefs.current.get(task.id);
      if (!pred || !succ) continue;
      const pr = pred.getBoundingClientRect();
      const sr = succ.getBoundingClientRect();
      lines.push({
        x1: pr.right - cr.left + wrap.scrollLeft,
        y1: pr.top + pr.height / 2 - cr.top + wrap.scrollTop,
        x2: sr.left - cr.left + wrap.scrollLeft,
        y2: sr.top + sr.height / 2 - cr.top + wrap.scrollTop,
      });
    }
    setDepLines(lines);
    setSvgSize({ w: wrap.scrollWidth, h: wrap.scrollHeight });
  }, [sortedTasks]);

  useLayoutEffect(() => {
    measureDependencyLines();
  }, [measureDependencyLines, loading, error, rangeMin, rangeMax]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measureDependencyLines());
    ro.observe(el);
    el.addEventListener('scroll', measureDependencyLines);
    window.addEventListener('resize', measureDependencyLines);
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measureDependencyLines);
      window.removeEventListener('resize', measureDependencyLines);
    };
  }, [measureDependencyLines]);

  if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
    return <p className="project-detail-panel-placeholder">Sign in to view the timeline.</p>;
  }

  return (
    <div className="project-timeline-tab" role="region" aria-label="Project timeline">
      {loading && <p className="project-detail-panel-placeholder">Loading timeline…</p>}
      {error && (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && tasks.length === 0 && (
        <p className="project-detail-panel-placeholder">No tasks on this project yet. Add tasks from the Board tab.</p>
      )}

      {!loading && !error && sortedTasks.length > 0 && (
        <div className="project-gantt">
          <div className="project-gantt-head">
            <div className="project-gantt-corner" aria-hidden="true" />
            <div className="project-gantt-axis">
              {ticks.map((ms, i) => (
                <span key={`tick-${i}-${ms}`} className="project-gantt-tick">
                  {formatTick(ms)}
                </span>
              ))}
            </div>
          </div>

          <div className="project-gantt-body" ref={bodyRef}>
            {svgSize.w > 0 && svgSize.h > 0 && (
              <svg
                className="project-gantt-dep-svg"
                width={svgSize.w}
                height={svgSize.h}
                aria-hidden
                focusable="false"
              >
                <defs>
                  <marker
                    id={markerId}
                    markerWidth="8"
                    markerHeight="8"
                    refX="6"
                    refY="4"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L8,4 L0,8 z" fill="#64748b" />
                  </marker>
                </defs>
                {depLines.map((l, i) => {
                  const midX = (l.x1 + l.x2) / 2;
                  return (
                    <path
                      key={i}
                      d={`M ${l.x1} ${l.y1} C ${midX} ${l.y1}, ${midX} ${l.y2}, ${l.x2} ${l.y2}`}
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      markerEnd={`url(#${markerId})`}
                    />
                  );
                })}
              </svg>
            )}

            {sortedTasks.map((task) => {
              const tr = taskTimeRange(task);
              const { leftPct, widthPct, className } = barStyle(task, rangeMin, rangeMax);
              const colId = task.project_board_column_id;
              const sectionLabel =
                colId != null && colId !== ''
                  ? boardColumnTitleById.get(colId) ?? colId
                  : '—';
              const assignee = assigneeLabel(task);
              const labelTitle = `Open task #${task.id}: ${assignee}; ${sectionLabel}`;
              return (
                <div key={task.id} className="project-gantt-row">
                  <button
                    type="button"
                    className="project-gantt-label"
                    onClick={() =>
                      navigate(`/tasks/${task.id}`, { state: { from: location.pathname + location.search } })
                    }
                    title={labelTitle}
                    aria-label={`${task.name}. Assigned: ${assignee}. Section: ${sectionLabel}.`}
                  >
                    <span className="project-gantt-label-text">{task.name}</span>
                    <div className="project-gantt-label-details">
                      <span className="project-gantt-label-meta">{assignee}</span>
                      <span className="project-gantt-label-meta project-gantt-label-section">{sectionLabel}</span>
                    </div>
                  </button>
                  <div className="project-gantt-track">
                    <div
                      ref={(el) => {
                        if (el) barRefs.current.set(task.id, el);
                        else barRefs.current.delete(task.id);
                      }}
                      className={className}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      title={`${task.name}: ${formatTick(tr.startMs)} – ${formatTick(tr.endMs)}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTimelineTab;
